import React, { useState, useEffect, useRef } from "react";
import { useSelector } from 'react-redux';
import { Badge, Button, Checkbox, CheckPicker, Col, DateRangePicker, Divider, Grid, Message, Panel, Row, Stack, useToaster } from 'rsuite';
import { BarChart, YAxis, Bars, PieChart, FunnelChart, Funnel } from '@rsuite/charts';
import { Base64 } from 'js-base64';
import axios from "axios";
import subDays from 'date-fns/subDays';
import addDays from 'date-fns/addDays';
import startOfMonth from 'date-fns/startOfMonth';
import RefreshIcon from '@rsuite/icons/legacy/Check';
import VisitsIcon from 'assets/img/stats_pageviews.svg';
import UVIcon from 'assets/img/stats_uv.svg';
import ViewsIcon from 'assets/img/stats_views.svg';
const { combine, afterToday, before } = DateRangePicker;

const dateToDMY = (date) => {
    let d = date.getDate(), m = date.getMonth() + 1, y = date.getFullYear();
    return '' + (d <= 9 ? '0' + d : d) + '-' + (m <= 9 ? '0' + m : m) + '-' + y;
}
const LocaleDate = {
    sunday: 'Do',
    monday: 'Lu',
    tuesday: 'Ma',
    wednesday: 'Wi',
    thursday: 'Ju',
    friday: 'Vi',
    saturday: 'Sa',
    ok: 'Listo',
    today: 'Hoy',
    yesterday: 'Ayer',
    hours: 'Horas',
    minutes: 'Minutos',
    seconds: 'Segundos',
    last7Days: 'Últimos 7 días'
};
const predefinedRanges = [
    {
        label: 'Hoy',
        value: [new Date(), new Date()],
        placement: 'left'
    },
    {
        label: 'Ayer',
        value: [addDays(new Date(), -1), addDays(new Date(), -1)],
        placement: 'left'
    },
    {
        label: 'Últimos 7 días',
        value: [subDays(new Date(), 6), new Date()],
        placement: 'left'
    },
    {
        label: 'Últimos 30 días',
        value: [subDays(new Date(), 29), new Date()],
        placement: 'left'
    },
    {
        label: 'Éste mes',
        value: [startOfMonth(new Date()), new Date()],
        placement: 'left'
    },
];

function Dashboard({ setWorking }) {
    const site = useSelector(state => state.site);
    const user = useSelector(state => state.auth.user)
    const apiURL = site.site.apiServer;
    const [between, setBetween] = useState([addDays(new Date(), -7), new Date()]);

    const toaster = useToaster();
    const messageError = (value) => (<Message showIcon type="error" closable>Ocurrió un error al intentar obtener {value}</Message>);


    const [dataGlobal, setDataGlobal] = useState({ publications: 0, pageViews: 0, uniqueViews: 0 });
    const [dataRankUser, setDataRankUser] = useState([]);
    const [dataRankPub, setDataRankPub] = useState([]);

    const [canFetch, setCanFetch] = useState(false);

    const [selectedUsers, setSelectedUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [users, setUsers] = useState([]);
    const allUsers = useRef([]);
    const usersRef = useRef();
    const footerStyles = {
        padding: '10px 2px',
        borderTop: '1px solid #e5e5e5'
    };
    const footerButtonStyle = {
        float: 'right',
        marginRight: 10,
        marginTop: 2
    };

    const fetchDataFilter = async () => await axios.all([
        axios.get(apiURL + "/usuario/list", { ref: 'usuario', headers: { token: JSON.parse(atob(localStorage.getItem("token"))).at }, })
            .catch((e) => { setUsers([]); setLoadingUsers(false); console.log(e); toaster.push(messageError('usuarios'), { placement: "topCenter", duration: 3000 }); }),
    ]).then((responses) => {
        responses.filter(resp => resp !== undefined).forEach((resp) => {
            if (resp.config.ref === 'usuario') {
                setUsers(resp.data.data);
                allUsers.current = resp.data.data.map(item => item.value);
                setLoadingUsers(false);
                setSelectedUsers(allUsers.current);
                setCanFetch(true);
            }
        });
    });

    const fetchData = async (_between) => {
        setWorking(true);
        if (_between == null)
            _between = between;
        else
            setBetween(_between);
        const users = Base64.encode(selectedUsers.join(','));
        await axios
            .get(`${apiURL}/catalogo/dashboard/?from=${_between[0].toISOString()}&to=${_between[1].toISOString()}&users=${users}`,
                { headers: { token: JSON.parse(atob(localStorage.getItem("token"))).at }, })
            .then((response) => {
                console.log(response);
                setDataGlobal(response.data.global[0]);
                setDataRankUser(response.data.rankUser);
                setDataRankPub(response.data.rankPub);
                setWorking(false);
            })
            .catch((e) => { console.log(e) })
            .finally(() => { });

    }

    useEffect(() => {
        if (canFetch)
            fetchData(between);
    }, [canFetch])

    useEffect(() => {
        fetchDataFilter();
    }, []);

    return (
        <>
            <Panel header={<Stack alignItems="baseline" spacing={10}>
                <Stack.Item grow={1}>
                    <h4 className="title">Dashboard</h4>
                </Stack.Item>
                <CheckPicker
                    ref={usersRef} preventOverflow={true} value={selectedUsers} style={{width:300}} appearance="subtle" size="sm"
                    locale={{ searchPlaceholder: "Buscar usuario", noResultsText: "No se econtraron resultados" }}
                    onChange={value => { setSelectedUsers(value); console.log('asdadad'); }} placeholder="Seleccionar" loading={loadingUsers}
                    label="Usuarios" data={users} placement="autoVerticalEnd" sticky renderExtraFooter={() => (
                        <div style={footerStyles}>
                            <Checkbox
                                indeterminate={selectedUsers.length > 0 && selectedUsers.length < allUsers.current.length}
                                checked={selectedUsers.length === allUsers.current.length}
                                onChange={(value, checked) => setSelectedUsers(checked ? allUsers.current : [])}
                            >
                                Todos
                            </Checkbox>
                            <Button style={footerButtonStyle} appearance="primary" size="sm" onClick={() => usersRef.current.close()}>
                                Listo
                            </Button>
                        </div>
                    )} />
                <Divider vertical />
                <DateRangePicker format="dd-MM-yyyy" placement="auto" locale={LocaleDate} appearance="subtle" size="sm" cleanable={false} editable={false}
                    shouldDisableDate={combine(before(90), afterToday())} preventOverflow={true} ranges={predefinedRanges}
                    renderValue={(value) => ('Período ' + dateToDMY(new Date(value[0].toISOString())) + ' al ' + dateToDMY(new Date(value[1].toISOString())))}
                    onChange={(value) => setBetween(value)} value={between} defaultCalendarValue={[new Date(new Date().setHours(0, 0, 0, 0)), new Date(new Date().setHours(23, 59, 59, 0))]} />
                <Divider vertical />
                <Button appearance="primary" startIcon={<RefreshIcon />} size="sm" onClick={() => { fetchData(); }}>
                    Aplicar
                </Button>
            </Stack>} bordered>
                <div>
                    <Grid fluid>
                        <Row style={{marginBottom: 10}}>
                            <Col xs={24} sm={24} md={8}>
                                <Panel bordered style={{ textAlign: 'right', backgroundColor: 'rgba(0,0,0,.02)' }}>
                                    <img src={VisitsIcon} style={{ position: "absolute", width: 100, left: 26, top: 34, opacity: .5 }} />
                                    <div>Publicaciones Visitadas</div>
                                    <div style={{ fontSize: 36 }}>{dataGlobal.pageViews}</div>
                                </Panel>
                            </Col>
                            <Col xs={24} sm={24} md={8}>
                                <Panel bordered style={{ textAlign: 'right', backgroundColor: 'rgba(0,0,0,.02)' }}>
                                    <img src={UVIcon} style={{ position: "absolute", width: 100, left: 26, top: 34, opacity: .5 }} />
                                    <div>Visitantes &Uacute;nicos</div>
                                    <div style={{ fontSize: 36 }}>{dataGlobal.uniqueViews}</div>
                                </Panel>
                            </Col>
                            <Col xs={24} sm={24} md={8}>
                                <Panel bordered style={{ textAlign: 'right', backgroundColor: 'rgba(0,0,0,.02)' }}>
                                    <img src={ViewsIcon} style={{ position: "absolute", width: 100, left: 26, top: 34, opacity: .5 }} />
                                    <div>Publicaciones realizadas</div>
                                    <div style={{ fontSize: 36 }}>{dataGlobal.publications}</div>
                                </Panel>
                            </Col>
                        </Row>
                        <Row style={{ marginBottom: 10 }}>
                            <Col xs={24} sm={24} md={16}>
                                <Panel bordered header={<><h5>Ranking por publicación</h5><small>Publicacion más visitadas</small></>}>
                                    <Grid fluid>
                                        {dataRankPub.length > 0 && (
                                            <Row style={{ marginBottom: 10, fontWeight: 'bold' }}>
                                                <Col md={8}>
                                                    Usuario
                                                </Col>
                                                <Col md={6}>
                                                    Publicación
                                                </Col>
                                                <Col md={6}>

                                                </Col>
                                                <Col md={2}>
                                                    <img src={VisitsIcon} style={{ opacity: .5, width: '100%', maxHeight: 22 }} />
                                                </Col>
                                                <Col md={2}>
                                                    <img src={UVIcon} style={{ opacity: .5, width: '100%', maxHeight: 22 }} />
                                                </Col>
                                            </Row>)}
                                        {dataRankPub.length > 0 && dataRankPub.map((a, v) => (
                                            <Row style={{ marginBottom: 5 }} key={'key_' + v}>
                                                <Col md={8}>
                                                    {a.user}
                                                </Col>
                                                <Col md={6}>
                                                    {a.name}
                                                </Col>
                                                <Col md={6}>
                                                    <a target="_blank" href={window.location.origin + '/vc/' + a.pubKey} style={{padding:0}} className="rs-btn rs-btn-link">{a.pubKey}</a>
                                                </Col>
                                                <Col md={2} style={{ textAlign: 'center' }}>
                                                    {a.pageViews}
                                                </Col>
                                                <Col md={2} style={{ textAlign: 'center' }}>
                                                    {a.uniqueViews}
                                                </Col>
                                            </Row>)
                                        )}
                                        {dataRankPub.length == 0 && (
                                            <Row style={{ marginBottom: 5 }}>
                                                <Col md={24} style={{ textAlign: 'center' }}>Sin datos</Col>
                                            </Row>
                                        )}
                                    </Grid>
                                </Panel>
                            </Col>
                            <Col xs={24} sm={24} md={8}>
                                <Panel bordered header={<><h5>Ranking por usuario</h5><small>Publicaciones más visitadas</small></>}>
                                    <Grid fluid>
                                        {dataRankUser.length > 0 && (
                                            <Row style={{ marginBottom: 10, fontWeight: 'bold' }}>
                                                <Col md={12}>
                                                    Usuario
                                                </Col>
                                                <Col md={4}>
                                                    <img src={VisitsIcon} style={{ opacity: .5, width: '100%', maxHeight: 22 }} />
                                                </Col>
                                                <Col md={4}>
                                                    <img src={UVIcon} style={{ opacity: .5, width: '100%', maxHeight: 22 }} />
                                                </Col>
                                                <Col md={4}>
                                                    <img src={ViewsIcon} style={{ opacity: .5, width: '100%', maxHeight: 22 }} />
                                                </Col>
                                            </Row>)}
                                        {dataRankUser.length > 0 && dataRankUser.map((a, v) => (
                                            <Row style={{ marginBottom: 5 }} key={'key_' + v}>
                                                <Col md={12}>
                                                    {a.user}
                                                </Col>
                                                <Col md={4} style={{ textAlign: 'center' }}>
                                                    {a.pageViews}
                                                </Col>
                                                <Col md={4} style={{ textAlign: 'center' }}>
                                                    {a.uniqueViews}
                                                </Col>
                                                <Col md={4} style={{ textAlign: 'center' }}>
                                                    {a.publications}
                                                </Col>
                                            </Row>)
                                        )}
                                        {dataRankUser.length == 0 && (
                                            <Row style={{ marginBottom: 5 }}>
                                                <Col md={24} style={{ textAlign:'center' }}>Sin datos</Col>
                                            </Row>
                                         )}
                                    </Grid>
                                </Panel>
                            </Col>
                        </Row>
                        
                    </Grid>
                </div>
            </Panel>
        </>
    );
}
export default Dashboard;